import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuthStore } from '@stores/auth-store';

import Header from '../../components/header/header.component';
import ModuleSummary from '../../components/module-summary/module-summary.component';
import ResponseCodes from '../../constants/response.codes';
import { paymentSdk } from '../../services/payment.service';
import NumberUtil, { Currency } from '../../utils/number.util';
import { Payment } from '../../types/payment.type';
import Table from '../../components/table/table.component';
import { TableRow } from '../../types/table-row.type';
import { mapPaymentStatus } from '../../utils/payment-status.util';
import { biSdk } from '../../services/bi.service';
import BasicButton from '../../components/basic-button/basic-button.component';
import FilteringButton from '../../components/filtering-button/filtering-button.component';
import { useThemeStore } from '../../store';
import DownloadExcelButtonComponent from '../../components/download-excel-button/download-excel-button.component';
import View from '../../components/view/view';
import { RolePermission } from '../../types/user.type';

import styles from './payments.module.scss';

const TotalsMapper: { [key: string]: string } = {
    totalApproved: 'Pagos aprobados de la semana',
    totalPending: 'Total pagos pendientes de la semana',
    totalInMonth: 'Total pagos de la semana',
    totalRevenue: 'Revenue total',
};

const PaymentsView = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams({ page: '1', count: '10' });

    const { theme } = useThemeStore();
    const { permissions } = useAuthStore();

    const [totalPages, setTotalPages] = useState(1);
    const [tableRows, setTableRows] = useState<TableRow[]>([]);
    const [totalSummary, setTotalSummary] = useState([
        { label: 'Pagos aprobados de la semana', value: '-' },
        { label: 'Total pagos pendientes', value: '-' },
        { label: 'Total pagos de la semana', value: '-' },
        { label: 'Fecha último pago', value: '-' },
    ]);

    useEffect(() => {
        getTotals(permissions);
    }, [permissions]);

    useEffect(() => {
        fetchPayments(mapParams(params), permissions);
    }, [params, permissions]);

    const mapParams = useCallback((params: URLSearchParams) => {
        const filters: Record<string, any> = {};
        for (const [key, value] of params.entries()) {
            filters[key] = value;
        }

        return filters;
    }, []);

    const canRead = useCallback((permissions: RolePermission[]) => {
        const permission = permissions.find(
            (per) => per.name === 'read-payments'
        );
        return Boolean(permission);
    }, []);

    const canCreate = useCallback((permissions: RolePermission[]) => {
        const permission = permissions.find(
            (per) => per.name === 'create-payments'
        );
        return Boolean(permission);
    }, []);

    const getTotals = async (permissions: RolePermission[]) => {
        if (!canRead(permissions)) return;

        const response = await biSdk.getTotalPayments();
        if (response.code !== ResponseCodes.PROCESS_OK) {
            return console.error('Error getting payments:', response);
        }
        const totals = response.data;
        const summary = Object.keys(totals).map((key) => ({
            label: TotalsMapper[key],
            value: totals[key],
        }));
        setTotalSummary(summary);
    };

    const fetchPayments = useCallback(
        async (queryParams: any, permissions: RolePermission[]) => {
            if (!canRead(permissions)) return;

            const response = await paymentSdk.getAll(queryParams);
            if (response.code !== ResponseCodes.PROCESS_OK) {
                return console.error('Error getting payments:', response);
            }
            const { total } = response.data;
            setTotalPages(Math.ceil(total / Number(params.get('count'))));
            mapRows(response.data.result);
        },
        [params]
    );

    const mapRows = (payments: Payment[]) => {
        const paymentRow = payments.map((payment) => {
            return {
                id: payment.id,
                data: [
                    { type: 'number', value: payment.id },
                    {
                        type: 'string',
                        value: `${payment.payer?.firstName} ${
                            payment.payer?.lastName ?? ''
                        }`,
                    },
                    {
                        type: 'string',
                        value: NumberUtil.formatCurrency(
                            payment.amount * payment.exchangeRate,
                            (payment.currency as Currency) ?? 'USD'
                        ),
                    },
                    { type: 'status', value: mapPaymentStatus(payment.status) },
                    {
                        type: 'date',
                        value: new Date(payment.createdAt),
                    },
                ],
            };
        });

        setTableRows(paymentRow);
    };

    const goToPage = useCallback(
        (page: number) => {
            if (page < 1 || page > totalPages) return;

            params.set('page', page.toString());
            setParams(params);
        },
        [params, totalPages]
    );

    const goToCreatePayment = useCallback(() => {
        return navigate('/payments/create');
    }, [navigate]);

    const addFilter = useCallback(
        (attr: string, value: string) => {
            if (!value || value.toString().trim().length === 0) {
                params.delete(attr);
            } else {
                params.set(attr, value);
            }
            params.set('page', '1');
            setParams(params);
        },
        [params]
    );

    return (
        <View permissions={permissions} requiredPermission="read-payments">
            <Header
                mode={theme}
                title={'Pagos'}
                subtitle={'Aquí puedes ver el estado de los pagos'}
            />
            <ModuleSummary mode={theme} summary={totalSummary} />
            <article className={styles.mainButtonsContainer}>
                <BasicButton
                    onClick={goToCreatePayment}
                    disabled={!canCreate(permissions)}
                    disabledReason="No tienes permisos"
                >
                    Registrar nuevo pago
                </BasicButton>
                <DownloadExcelButtonComponent entity={'payments'} />
            </article>
            <div className={'view-filtering-section'}>
                <span className={'filtering-label'}>Buscar por:</span>
                <FilteringButton
                    defaultValue={params.get('id')}
                    text={'N° Pago'}
                    onSetFilter={(id) => addFilter('id', id)}
                />
                <FilteringButton
                    defaultValue={params.get('amount')}
                    text={'Monto'}
                    onSetFilter={(amount) => addFilter('amount', amount)}
                />
                <FilteringButton
                    defaultValue={params.get('days')}
                    text={'Fecha'}
                    type={'date'}
                    onSetFilter={(days) => addFilter('days', days)}
                />
            </div>
            <Table
                mode={theme}
                header={[
                    'N° Pago',
                    'Pagador',
                    'Total',
                    'Estado',
                    'Fecha creación',
                ]}
                data={tableRows}
                currentPage={Number(params.get('page'))}
                totalPages={totalPages}
                onPageChange={goToPage}
                onItemSelected={(id) => navigate(`/payments/${id}`)}
            />
        </View>
    );
};

export default PaymentsView;
